import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Chip, Tooltip } from '@mui/material';
import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import { StyledTableContainer } from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import InvoicePreview from 'molecules/InvoicePreview';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { formatGCPUsageMonth, normalizeGCPBillingStatus } from '../status';
import {
  GCPBillingDataRow,
  GCPBillingPagination,
  GCPBillingPageSize,
  GCPBillingStatusKind,
} from '../types';
import { palette } from 'utils/styles/variables';
import { ActionContainerStyled } from 'organisms/InvoicesTable/InvoicesTable.styles';
import { LoadingButton } from '@mui/lab';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Link from 'atoms/Link/Link';

const statusColor: Record<
  GCPBillingStatusKind,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  created: 'success',
  processing: 'info',
  skipped: 'warning',
  failed: 'error',
  unknown: 'default',
};

interface GCPBillingTableProps {
  rows: GCPBillingDataRow[];
  total: number;
  loading: boolean;
  pagination: GCPBillingPagination;
  resendingId?: string | null;
  emptyText: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: GCPBillingPageSize) => void;
  onOpenResend: (row: GCPBillingDataRow) => void;
}

const valueOrDash = (value?: string) => value || '—';

const GCPBillingTable: FC<GCPBillingTableProps> = ({
  rows,
  total,
  loading,
  pagination,
  resendingId,
  emptyText,
  onPageChange,
  onPerPageChange,
  onOpenResend,
}) => {
  const [invoicePreview, setInvoicePreview] = useState<{
    invoiceNumber: string;
    region: string;
  } | null>(null);
  const bodyIdBeforeInvoicePreview = useRef<string | null | undefined>(
    undefined
  );

  const lockBodyScroll = useCallback(() => {
    if (bodyIdBeforeInvoicePreview.current === undefined) {
      bodyIdBeforeInvoicePreview.current = document.body.getAttribute('id');
    }

    document.body.setAttribute('id', 'body');
  }, []);

  const restoreBodyScroll = useCallback(() => {
    const previousBodyId = bodyIdBeforeInvoicePreview.current;
    if (previousBodyId === undefined) return;

    if (previousBodyId === null) {
      document.body.removeAttribute('id');
    } else {
      document.body.setAttribute('id', previousBodyId);
    }

    bodyIdBeforeInvoicePreview.current = undefined;
  }, []);

  const handleInvoicePreviewClose = useCallback(() => {
    setInvoicePreview(null);
    restoreBodyScroll();
  }, [restoreBodyScroll]);

  useEffect(() => restoreBodyScroll, [restoreBodyScroll]);

  const columns = useMemo(
    () => [
      {
        key: 'usage_month',
        label: 'Usage Month',
        sortable: false,
        width: '100px',
        render: (row: GCPBillingDataRow) => (
          <StyledCellText
            $textType="bold"
            sx={{ fontSize: '0.813rem !important' }}
          >
            {formatGCPUsageMonth(row.usage_month)}
          </StyledCellText>
        ),
      },
      {
        key: 'company_name',
        label: 'Company Name',
        sortable: false,
        render: (row: GCPBillingDataRow) => {
          const companyName = valueOrDash(row.company_name);

          if (!row.company_name || row.company_hs_id === undefined) {
            return (
              <StyledCellText sx={{ fontSize: '0.813rem !important' }}>
                {companyName}
              </StyledCellText>
            );
          }

          return (
            <Link to={`/company/${row.company_hs_id}`} $textDecoration="none">
              <StyledCellText
                $textType="link"
                sx={{ fontSize: '0.813rem !important' }}
              >
                {companyName}
              </StyledCellText>
            </Link>
          );
        },
      },
      {
        key: 'sales_rep_name',
        label: 'Sales Rep',
        sortable: false,
        render: (row: GCPBillingDataRow) => (
          <StyledCellText sx={{ fontSize: '0.813rem !important' }}>
            {valueOrDash(row.sales_rep_name)}
          </StyledCellText>
        ),
      },
      {
        key: 'gcp_billing_id',
        label: 'GCP Billing ID',
        sortable: false,
        width: '150px',
        render: (row: GCPBillingDataRow) => (
          <StyledCellText sx={{ fontSize: '0.7rem !important' }}>
            {valueOrDash(row.gcp_billing_id)}
          </StyledCellText>
        ),
      },
      {
        key: 'currency',
        label: 'Currency',
        sortable: false,
        render: (row: GCPBillingDataRow) => (
          <StyledCellText sx={{ fontSize: '0.813rem !important' }}>
            {valueOrDash(row.currency)}
          </StyledCellText>
        ),
      },
      {
        key: 'total_amount',
        label: 'Total Amount',
        sortable: false,
        render: (row: GCPBillingDataRow) => (
          <StyledCellText sx={{ fontSize: '0.813rem !important' }}>
            {typeof row.total_amount === 'number' && row.currency
              ? currencyFormatter(row.total_amount, row.currency)
              : '—'}
          </StyledCellText>
        ),
      },
      {
        key: 'invoice_status',
        label: 'Invoice Status',
        sortable: false,
        render: (row: GCPBillingDataRow) => {
          const status = normalizeGCPBillingStatus(
            row.invoice_status,
            row.reason
          );
          return (
            <Chip
              color={statusColor[status.kind]}
              label={status.label}
              size="small"
              sx={{ fontSize: '0.67rem' }}
            />
          );
        },
      },
      {
        key: 'invoice_number',
        label: 'Invoice Number',
        sortable: false,
        render: (row: GCPBillingDataRow) => {
          const invoiceNumber = row.invoice_number;
          const region = row.qb_region;

          if (!invoiceNumber || !region) return '-';

          return (
            <StyledCellText
              $color={palette.primary.main}
              sx={{
                display: 'inline',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={(event) => {
                event?.preventDefault();
                event?.stopPropagation();

                setInvoicePreview({
                  invoiceNumber,
                  region,
                });
                lockBodyScroll();
              }}
            >
              #{invoiceNumber}
            </StyledCellText>
          );
        },
      },
      {
        key: 'reason',
        label: 'Reason for Failure / Skip Notice',
        sortable: false,
        render: (row: GCPBillingDataRow) => (
          <StyledCellText sx={{ fontSize: '0.813rem !important' }}>
            {valueOrDash(row.reason)}
          </StyledCellText>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        render: (row: GCPBillingDataRow) => {
          const status = normalizeGCPBillingStatus(
            row.invoice_status,
            row.reason
          );
          const canTrigger = status.kind !== 'created';
          return canTrigger ? (
            <ActionContainerStyled>
              <Tooltip title="Trigger Invoice Creation">
                <LoadingButton
                  disabled={resendingId === row.id}
                  loading={resendingId === row.id}
                  sx={{
                    p: 0,
                    fontSize: '0.875rem',
                    minWidth: '30px !important',
                    '&:hover': { background: 'transparent' },
                  }}
                  onClick={() => onOpenResend(row)}
                >
                  <ReceiptIcon />
                </LoadingButton>
              </Tooltip>
            </ActionContainerStyled>
          ) : null;
        },
      },
    ],
    [lockBodyScroll, onOpenResend, resendingId]
  );

  return (
    <>
      <StyledTableContainer $new>
        <Table
          idKey="id"
          loading={loading}
          columns={columns}
          data={rows as unknown as Array<{ [key: string]: unknown }>}
          emptyText={emptyText}
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          count={total}
          rowsPerPage={pagination.perPage}
          page={total ? pagination.page - 1 : 0}
          rowsPerPageOptions={[]}
          onPageChange={(_, page) => onPageChange(page + 1)}
          ActionsComponent={(props) => (
            <PaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100]}
              onRowsPerPageChange={(value) =>
                onPerPageChange(value as GCPBillingPageSize)
              }
            />
          )}
        />
      </StyledTableContainer>
      {invoicePreview && (
        <InvoicePreview
          invoiceNumber={invoicePreview.invoiceNumber}
          region={invoicePreview.region}
          onClose={handleInvoicePreviewClose}
        />
      )}
    </>
  );
};

export default GCPBillingTable;
